import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Search, X } from "lucide-react";
import "../../styles/search-select.css";

// ================================================================
//  SearchSelect — a searchable dropdown (combobox)
//
//  <SearchSelect
//      options={[{ value: 1, label: "Rajesh", sub: "9847012345", meta: 4 }]}
//      value={selectedId}            // matched with String(), so 1 === "1"
//      onChange={(val) => ...}       // "" when cleared
//      placeholder="Select owner"
//      clearable                     // shows an × to reset to ""
//      error                         // red border
//  />
//
//  - Type to filter by label or sub text (e.g. mobile number)
//  - ↑ ↓ to move, Enter to pick, Esc to close
//  - Popover is rendered in a portal, so it is never clipped by
//    scrolling modals; on phones it opens as a bottom sheet.
// ================================================================

const MOBILE_QUERY = "(max-width: 640px)";
const POPOVER_MAX_H = 320;

const norm = (v) => (v === null || v === undefined ? "" : String(v)).toLowerCase();

const initials = (label) =>
    String(label || "?")
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase();

// Stable soft colour per name, for the little avatar circle
const AVATAR_COLORS = ["#2563eb", "#7c3aed", "#0891b2", "#059669", "#d97706", "#db2777", "#4f46e5", "#0d9488"];
const avatarColor = (label) => {
    let h = 0;
    for (const ch of String(label || "")) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
    return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

const Highlight = ({ text, query }) => {
    const q = query.trim();
    if (!q) return text;
    const i = norm(text).indexOf(q.toLowerCase());
    if (i < 0) return text;
    return (
        <>
            {text.slice(0, i)}
            <mark className="ss-mark">{text.slice(i, i + q.length)}</mark>
            {text.slice(i + q.length)}
        </>
    );
};

const SearchSelect = ({
    options = [],
    value,
    onChange,
    placeholder = "Select…",
    searchPlaceholder = "Search…",
    emptyText = "No matches found",
    clearable = false,
    error = false,
    disabled = false,
    id,
    className = "",
    "aria-label": ariaLabel,
}) => {
    const autoId = useId();
    const listId = `${id || autoId}-list`;
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [active, setActive] = useState(0);
    const [pos, setPos] = useState(null);
    const [isMobile, setIsMobile] = useState(
        () => typeof window !== "undefined" && window.matchMedia?.(MOBILE_QUERY).matches
    );

    const triggerRef = useRef(null);
    const popRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    const selected = options.find((o) => String(o.value) === String(value ?? "")) || null;
    const hasValue = value !== "" && value !== null && value !== undefined && !!selected;

    const filtered = useMemo(() => {
        const q = norm(query).trim();
        if (!q) return options;
        const qDigits = /^[\d\s+\-()]+$/.test(q) ? q.replace(/\D/g, "") : "";
        return options.filter((o) => {
            if (norm(o.label).includes(q) || norm(o.sub).includes(q)) return true;
            return qDigits && norm(o.sub).replace(/\D/g, "").includes(qDigits);
        });
    }, [options, query]);

    // ── open / close ──────────────────────────────────────────────
    const openMenu = () => {
        if (disabled) return;
        setQuery("");
        const idx = options.findIndex((o) => String(o.value) === String(value ?? ""));
        setActive(idx >= 0 ? idx : 0);
        setOpen(true);
    };

    const closeMenu = useCallback((refocus = true) => {
        setOpen(false);
        if (refocus) triggerRef.current?.focus();
    }, []);

    const pick = (opt) => {
        onChange?.(opt ? opt.value : "");
        closeMenu();
    };

    // ── positioning (desktop popover) ─────────────────────────────
    const place = useCallback(() => {
        const el = triggerRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const spaceBelow = window.innerHeight - r.bottom;
        const spaceAbove = r.top;
        const up = spaceBelow < POPOVER_MAX_H + 16 && spaceAbove > spaceBelow;
        const width = Math.max(r.width, 260);
        const left = Math.min(r.left, window.innerWidth - width - 8);
        setPos({
            left: Math.max(8, left),
            width,
            top: up ? undefined : r.bottom + 6,
            bottom: up ? window.innerHeight - r.top + 6 : undefined,
            maxHeight: Math.max(180, Math.min(POPOVER_MAX_H, (up ? spaceAbove : spaceBelow) - 16)),
        });
    }, []);

    useLayoutEffect(() => {
        if (!open) return;
        place();
        const onMove = () => place();
        window.addEventListener("resize", onMove);
        window.addEventListener("scroll", onMove, true);
        return () => {
            window.removeEventListener("resize", onMove);
            window.removeEventListener("scroll", onMove, true);
        };
    }, [open, place]);

    useEffect(() => {
        const mq = window.matchMedia?.(MOBILE_QUERY);
        if (!mq) return;
        const fn = (e) => setIsMobile(e.matches);
        mq.addEventListener?.("change", fn);
        return () => mq.removeEventListener?.("change", fn);
    }, []);

    // Focus the search box when opening
    useEffect(() => {
        if (open) requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
    }, [open]);

    // Click outside closes
    useEffect(() => {
        if (!open) return;
        const onDown = (e) => {
            if (popRef.current?.contains(e.target) || triggerRef.current?.contains(e.target)) return;
            closeMenu(false);
        };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("touchstart", onDown, { passive: true });
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("touchstart", onDown);
        };
    }, [open, closeMenu]);

    // Keep the highlighted row in view
    useEffect(() => {
        if (!open) return;
        listRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: "nearest" });
    }, [active, open]);

    useEffect(() => { setActive(0); }, [query]);

    // ── keyboard ──────────────────────────────────────────────────
    const onTriggerKey = (e) => {
        if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) {
            e.preventDefault();
            openMenu();
        } else if ((e.key === "Backspace" || e.key === "Delete") && clearable && hasValue) {
            e.preventDefault();
            onChange?.("");
        }
    };

    const onSearchKey = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((i) => Math.min(i + 1, filtered.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (filtered[active]) pick(filtered[active]);
        } else if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            closeMenu();
        } else if (e.key === "Tab") {
            closeMenu(false);
        }
    };

    // ── render ────────────────────────────────────────────────────
    const popover = open && (isMobile || pos) && createPortal(
        <>
            {isMobile && <div className="ss-sheet-backdrop" onClick={() => closeMenu(false)} />}
            <div
                ref={popRef}
                className={`ss-popover${isMobile ? " ss-sheet" : ""}`}
                style={isMobile ? undefined : { left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom }}
                role="dialog"
                aria-label={ariaLabel || placeholder}
            >
                {isMobile && (
                    <div className="ss-sheet-head">
                        <span className="ss-sheet-grip" />
                        <div className="ss-sheet-title">
                            <span>{ariaLabel || placeholder}</span>
                            <button type="button" className="ss-icon-btn" onClick={() => closeMenu(false)} aria-label="Close">
                                <X size={18} />
                            </button>
                        </div>
                    </div>
                )}
                <div className="ss-search">
                    <Search size={15} className="ss-search-icon" />
                    <input
                        ref={inputRef}
                        type="text"
                        className="ss-search-input"
                        placeholder={searchPlaceholder}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={onSearchKey}
                        role="combobox"
                        aria-expanded="true"
                        aria-controls={listId}
                        aria-activedescendant={filtered[active] ? `${listId}-${active}` : undefined}
                        autoComplete="off"
                    />
                    {query && (
                        <button type="button" className="ss-icon-btn ss-search-clear" onClick={() => { setQuery(""); inputRef.current?.focus(); }} aria-label="Clear search">
                            <X size={14} />
                        </button>
                    )}
                </div>

                <ul
                    ref={listRef}
                    id={listId}
                    className="ss-list"
                    role="listbox"
                    style={isMobile ? undefined : { maxHeight: pos.maxHeight - 58 }}
                >
                    {filtered.length === 0 ? (
                        <li className="ss-empty">{emptyText}</li>
                    ) : (
                        filtered.map((o, i) => {
                            const isSel = String(o.value) === String(value ?? "");
                            return (
                                <li
                                    key={o.value}
                                    id={`${listId}-${i}`}
                                    data-idx={i}
                                    role="option"
                                    aria-selected={isSel}
                                    className={`ss-option${i === active ? " ss-active" : ""}${isSel ? " ss-selected" : ""}`}
                                    onMouseEnter={() => setActive(i)}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => pick(o)}
                                >
                                    <span className="ss-avatar" style={{ background: avatarColor(o.label) }}>{initials(o.label)}</span>
                                    <span className="ss-option-text">
                                        <span className="ss-option-label"><Highlight text={String(o.label ?? "")} query={query} /></span>
                                        {o.sub && <span className="ss-option-sub"><Highlight text={String(o.sub)} query={query} /></span>}
                                    </span>
                                    {o.meta !== undefined && o.meta !== null && <span className="ss-badge">{o.meta}</span>}
                                    <Check size={16} className="ss-check" />
                                </li>
                            );
                        })
                    )}
                </ul>
                {options.length > 0 && (
                    <div className="ss-footer">
                        <span className="ss-count">{filtered.length} of {options.length}</span>
                        {!isMobile && <span className="ss-hint">↑↓ move · Enter select · Esc close</span>}
                    </div>
                )}
            </div>
        </>,
        document.body
    );

    return (
        <>
            <div
                ref={triggerRef}
                id={id}
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={ariaLabel}
                aria-disabled={disabled}
                className={`ss-trigger${open ? " ss-open" : ""}${hasValue ? " ss-has-value" : ""}${error ? " ss-error" : ""}${disabled ? " ss-disabled" : ""} ${className}`}
                onClick={() => (open ? closeMenu() : openMenu())}
                onKeyDown={onTriggerKey}
            >
                {hasValue ? (
                    <>
                        <span className="ss-avatar ss-avatar-sm" style={{ background: avatarColor(selected.label) }}>{initials(selected.label)}</span>
                        <span className="ss-value">{selected.label}</span>
                    </>
                ) : (
                    <span className="ss-placeholder">{placeholder}</span>
                )}
                {clearable && hasValue && (
                    <button
                        type="button"
                        className="ss-icon-btn ss-clear"
                        onClick={(e) => { e.stopPropagation(); onChange?.(""); }}
                        aria-label="Clear selection"
                        title="Clear"
                    >
                        <X size={14} />
                    </button>
                )}
                <ChevronDown size={16} className="ss-chevron" />
            </div>
            {popover}
        </>
    );
};

export default SearchSelect;